import { Delete as DeleteIcon } from "@mui/icons-material";
import Logger from "../../../services/Logger";
import { Checkbox, FormControl, FormControlLabel, FormGroup, IconButton, InputLabel, MenuItem, Select, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog";
import KeywordBox from "../../components/KeywordBox";
import PlayRowDetailDialog from "../../pzl_common/PlayRowDetailDialog";
import AdminGridService from "./AdminGridService";
import { getSolveColumns } from "./getSolveColumns";

export default function AdminGridPlay({ gridId }) {
    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 15 });
    const [sort, setSort] = useState([{ field: "createdAt", sort: "desc" }]);
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [keyword, setKeyword] = useState("");
    const [completionStatus, setCompletionStatus] = useState({
        all: true,
        completed: false,
        incomplete: false
    });
    const [onlyStarted, setOnlyStarted] = useState(false);
    const [testAccountFilter, setTestAccountFilter] = useState("exclude");
    const [excludeStaff, setExcludeStaff] = useState(true);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [, setSelectedRowDataForDelete] = useState([]);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedRowForDetail, setSelectedRowForDetail] = useState(null);

    useEffect(() => {
        const isCompleted = completionStatus.completed ? true : completionStatus.incomplete ? false : null;
        AdminGridService.requestPuzzlePlay(
            gridId, keyword, pageModel, sort, setRows, setRowCount, isCompleted, onlyStarted, testAccountFilter, excludeStaff
        );
    }, [pageModel, sort, gridId, keyword, completionStatus, onlyStarted, testAccountFilter, excludeStaff]);

    const handleCell = (cell) => {
        if (cell.field === 'id' || cell.field === '__check__' || cell.field == null) return;
        const rowData = rows.find(row => row.id === cell.id);
        if (rowData) {
            setSelectedRowForDetail(rowData);
            setDetailDialogOpen(true);
        }
    };

    const handleRowDoubleClick = (params) => {
        setSelectedRowForDetail(params.row);
        setDetailDialogOpen(true);
    };

    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setSelectedRowForDetail(null);
    };

    const handleSort = (model) => {
        setSort(model);
    };

    const handleSelectionModel = (newSelection) => {
        setSelectedRowIds(newSelection);
        // Find the selected row data for the delete dialog
        const selectedRowData = rows.filter(row => newSelection.includes(row.id));
        setSelectedRowDataForDelete(selectedRowData);
    };

    const handleCompletionStatusChange = (status) => {
        setCompletionStatus(prev => {
            const newStatus = { ...prev };

            if (status === 'all') {
                // If "All" is selected, uncheck others
                newStatus.all = true;
                newStatus.completed = false;
                newStatus.incomplete = false;
            } else {
                // If "Completed" or "Incomplete" is selected, uncheck "All"
                newStatus.all = false;
                newStatus[status] = !prev[status];
            }

            return newStatus;
        });
    };

    const handleDeleteClick = () => {
        if (selectedRowIds.length > 0) {
            setDeleteDialogOpen(true);
        }
    };

    const handleDeleteConfirm = async (targetIds) => {
        try {
            await AdminGridService.deleteGridPlay(targetIds);
            // Refresh the data after successful deletion
            const isCompleted = completionStatus.completed ? true : completionStatus.incomplete ? false : null;
            await AdminGridService.requestPuzzlePlay(
                gridId, keyword, pageModel, sort, setRows, setRowCount, isCompleted, onlyStarted, testAccountFilter, excludeStaff
            );
            setSelectedRowIds([]);
            setSelectedRowDataForDelete([]);
        } catch (error) {
            Logger.error('Error deleting grid play:', error);
            // You might want to show a snackbar or alert here
        }
    };

    const columns_solve = getSolveColumns();

    return (
        <Box sx={{ flexGrow: 1, height: "50%", display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, pt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <KeywordBox callback={setKeyword} />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="admin-grid-play-test-filter-label">Test accounts</InputLabel>
                        <Select
                            labelId="admin-grid-play-test-filter-label"
                            label="Test accounts"
                            value={testAccountFilter}
                            onChange={(e) => {
                                setTestAccountFilter(e.target.value);
                                setPageModel((prev) => ({ ...prev, page: 0 }));
                            }}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="exclude">Exclude tester</MenuItem>
                            <MenuItem value="include">Include tester</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={excludeStaff}
                                onChange={(e) => {
                                    setExcludeStaff(e.target.checked);
                                    setPageModel((prev) => ({ ...prev, page: 0 }));
                                }}
                                color="primary"
                            />
                        }
                        label="Exclude staff"
                    />
                    <FormGroup row>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={completionStatus.all}
                                    onChange={() => handleCompletionStatusChange('all')}
                                    color="primary"
                                />
                            }
                            label="All"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={completionStatus.completed}
                                    onChange={() => handleCompletionStatusChange('completed')}
                                    color="primary"
                                />
                            }
                            label="Completed"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={completionStatus.incomplete}
                                    onChange={() => handleCompletionStatusChange('incomplete')}
                                    color="primary"
                                />
                            }
                            label="Incomplete"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={onlyStarted}
                                    onChange={(e) => setOnlyStarted(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Only Started"
                        />
                    </FormGroup>
                </Box>
                <Tooltip title={`Delete ${selectedRowIds.length} selected row${selectedRowIds.length !== 1 ? 's' : ''}`}>
                    <span>
                        <IconButton
                            color="error"
                            onClick={handleDeleteClick}
                            disabled={selectedRowIds.length === 0}
                            sx={{ opacity: selectedRowIds.length > 0 ? 1 : 0.5 }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <DataGrid
                rows={rows}
                columns={columns_solve}
                rowCount={rowCount}
                pageSizeOptions={[15, 25, 30]}
                paginationMode="server"
                sortingMode="server"
                pagination={true}
                paginationModel={pageModel}
                onPaginationModelChange={(m) => setPageModel(m)}
                checkboxSelection
                experimentalFeatures={{ newEditingApi: false }}
                onCellClick={handleCell}
                onRowDoubleClick={handleRowDoubleClick}
                onSortModelChange={handleSort}
                onRowSelectionModelChange={handleSelectionModel}
                columnVisibilityModel={{ id: false }}
                hideFooterSelectedRowCount
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                }}
            />
            <PlayRowDetailDialog
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                rowData={selectedRowForDetail}
                puzzleKey="grid"
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Confirm Delete"
                message={`Are you sure you want to delete ${selectedRowIds.length} selected puzzle play record${selectedRowIds.length !== 1 ? 's' : ''}? This action cannot be undone.`}
                warningAlert="Don't use for user's play history. For testing purpose only."
                targetId={selectedRowIds}
                callback={handleDeleteConfirm}
            />
        </Box>
    );
}
